import { Payment } from 'payments-lib-types';

import { request } from './base';
import { snakify } from '@/helpers/common';
import { Country } from '@/helpers/conf';
import { CurrencyCode } from '@/helpers/currency';

// SUBSCRIPTIONS

export interface Subscriptions {
  receivePromos: boolean;
  receiveSmsPromos: boolean;
}

export type SubscriptionParams = Partial<Subscriptions>;

export const profileSubscriptions = () =>
  request<Subscriptions>({ url: 'subscriptions' });
export const updateProfileSubscriptions = (
  subscriptionParams: SubscriptionParams
) =>
  request<void>({
    method: 'PUT',
    url: 'subscriptions',
    data: snakify({ subscriptionParams }),
  });

// ACCOUNTS

export interface Account {
  currency: CurrencyCode;
  amountCents: number;
  availableToCashoutCents: number;
  availableToBetWithLockedCents: number;
  active: boolean;
}

export const playerAccounts = () =>
  request<Account[]>({ url: 'player/accounts?compatibility=false' });

// DOCUMENTS

export enum DocumentType {
  Identity = 'identity:',
  Address = 'address:',
  Payment = 'payment:',
}

export enum DocumentStatus {
  Pending = 'pending',
  Approved = 'approved',
  NotApproved = 'not_approved',
}

export interface Document {
  id: number;
  description: DocumentType;
  status: DocumentStatus;
  createdAt: string;
  updatedAt: string;
  fileName: string;
  declineReason?: string;
}

export const documents = () => request<Document[]>({ url: 'player/documents' });

export const removeDocument = (id: number) =>
  request<void>({
    method: 'DELETE',
    url: `player/documents/${id}`,
  });

export const createDocument = (documents: FormData) =>
  request<Document>({
    method: 'POST',
    url: 'player/documents',
    data: documents,
  });

// SESSIONS

export interface Session {
  id: number;
  ip: string;
  country: Country;
  userAgent: string;
  createdAt: string;
  current: boolean | null;
}

export const activeSessions = () =>
  request<Session[]>({ url: 'player/sessions' });

export interface ClosedSession extends Session {
  closedAt: string;
}

export const closedSessions = () =>
  request<ClosedSession[]>({ url: 'player/sessions/closed' });

export const removeSession = (id: number) =>
  request<void>({
    method: 'DELETE',
    url: `player/sessions/${id}`,
  });

// USER

export interface UpdatePasswordBody {
  currentPassword: string;
  email: string;
  password: string;
  passwordConfirmation: string;
}

export const updatePassword = (passwords: UpdatePasswordBody) =>
  request<{ errors: unknown }>({
    method: 'PUT',
    url: 'users',
    data: { user: snakify({ ...passwords }) },
  });

export const emailConfirmation = (email: string) =>
  request<void>({
    method: 'POST',
    url: 'users/confirmation',
    data: { user: { email } },
  });

// player/two_factor

export interface TwoFactor {
  data: string;
  otpSecret: string;
}

export const twoFactor = () => request<TwoFactor>({ url: 'player/two_factor' });

interface TwoFactorBody {
  authenticationCode: string;
  otpSecret: string;
}

export interface BackupCodes {
  backupCodes: string[];
}

export const activateTwoFactor = (codes: TwoFactorBody) =>
  request<BackupCodes>({
    method: 'POST',
    url: 'player/two_factor',
    data: snakify({ two_factor: { ...codes } }),
  });

export const deactivateTwoFactor = (authenticationCode: string) =>
  request<BackupCodes>({
    method: 'DELETE',
    url: 'player/two_factor',
    data: { two_factor: snakify({ authenticationCode }) },
  });

// GAMES

export interface HistoryGame {
  currency: CurrencyCode;
  totalBets: number;
  totalWins: number;
  game: string;
  title: string;
  provider: string;
  createdAt: string;
  finishedAt: string;
}

export const historyGames = () =>
  request<HistoryGame[]>({ url: 'player/games' });

// USER LIMITS

export interface LimitAccount {
  currency: CurrencyCode;
  amountCents: number;
  currentValueAmountCents: number;
  activeUntil: string;
}

export enum UserLimitStatus {
  Active = 'active',
  Confirmed = 'confirmed',
  Unconfirmed = 'unconfirmed',
  UnconfirmedDisable = 'unconfirmed_disable',
  ConfirmedDisable = 'confirmed_disable',
}

export type LimitPeriod = 'day' | 'week' | 'month';

export enum LimitType {
  Deposit = 'deposit',
  Loss = 'loss',
  CoolingOff = 'coolingoff',
  SelfExclusion = 'selfexclusion',
  Wager = 'wager',
}

export interface Limit {
  period: LimitPeriod;
  status: UserLimitStatus;
  disableAt: string | null;
  confirmUntil: string | null;
  parent_type: 'accounts';
  strategy: 'accounts';
  created_at: string;
  id: number;
  type: LimitType;
  accounts: LimitAccount[];
}

export const userLimits = () => request<Limit[]>({ url: 'user_limits' });

export const createUserLimit = (userLimit: any) =>
  request({ method: 'POST', url: 'user_limits', data: snakify({ userLimit }) });

export const deleteUserLimit = (id: number) =>
  request({ method: 'DELETE', url: `user_limits/${id}` });

export const userLimitsConfirm = (token: string) =>
  request<void>({
    method: 'POST',
    url: 'user_limits/confirm',
    data: { token },
  });

// PAYMENTS

export enum TransactionState {
  Accepted = 'accepted',
  Succeeded = 'succeeded',
  Rejected = 'rejected',
  Recalled = 'recalled',
  Reserved = 'reserved',
  Canceled = 'canceled',
  Pending = 'pending',
  Processing = 'processing',
  Declined = 'declined',
  Failed = 'failed',
}

export interface Transaction {
  id: number;
  amountCents: number;
  currency: CurrencyCode;
  action: Payment.Action;
  paymentSystem: string;
  recallable: false;
  createdAt: string;
  finishedAt: string;
  systemName: string;
  state: TransactionState;
  brand: string;
  success: boolean;
  cancellationReason: string;
}

export const playerPayments = () =>
  request<Transaction[]>({ url: 'player/payments' });

export const recallWithdraw = (id: number) =>
  request<void>({ method: 'POST', url: `player/payments/${id}/recall` });

// REFERRAL SYSTEM

export interface ReferralCode {
  refcode: string;
  claimedAmount: number;
  completedUsersCount: number;
  toClaimAmount: number;
  usersCount: number;
}

interface AggregatedData {
  totalInvited: number;
  totalClaimed: number;
}

export interface ReferralSystem {
  referralCodes: ReferralCode[];
  aggregatedData: AggregatedData;
  currency: string;
}

export const playerReferralSystem = (currency: string) =>
  request<ReferralSystem>({
    url: `player/referral_system/list?currency=${currency}`,
  });

export const createReferralCode = (refCode: string) =>
  request<BackupCodes>({
    method: 'POST',
    url: 'player/referral_system',
    data: snakify({ refCode }),
  });

export const claimReferralCode = (refCode: string) =>
  request<BackupCodes>({
    method: 'POST',
    url: 'player/referral_system/claim',
    data: snakify({ refCode }),
  });
